import { useWatch, useZionFormContext } from '@fs/zion-form'
import React from 'react'
import EventFormInput from './EventFormInput'

/**
 * FieldsSection
 *
 * Renders a dynamic list of form input fields for a specific field array path
 * (e.g., dates, places, or other fields) within an event form. It watches the
 * field array and renders an EventFormInput for each field in the array.
 *
 * This component is used to display and manage multiple fields of the same type
 * within an event, such as multiple date fields or multiple place fields.
 *
 * @param {Object} props
 * @param {string} props.path - Form field path to the array of fields to watch and render (e.g., 'events.primary.0.dates')
 * @param {string} props.indexName - Name identifier for the field type ('dates', 'places', or 'other')
 * @param {number} props.eventIndex - Index of the event in the events array
 * @param {React.ComponentType} props.SuggestComponent - The suggestion component to use for field input (e.g., DateSuggest, PlaceSuggest)
 * @param {string} props.suggestFieldType - Field type for the suggestion component (e.g., 'DATE', 'PLACE')
 * @param {string} props.suggestLabelKey - Translation key for the field label
 * @param {string} props.suggestDefaultLabel - Default label text if translation is not available
 * @param {boolean} props.primaryEvent - Whether this is a primary event (affects form behavior)
 * @param {string} props.splitScreenImageArk - ARK identifier for the split screen image context
 * @param {Function} props.onValidateFields - Callback function to validate fields, called when field values change
 * @returns {Array<JSX.Element>} Array of EventFormInput components, one for each field in the watched array
 */
const FieldsSection = ({
  path,
  indexName,
  eventIndex,
  SuggestComponent,
  suggestFieldType,
  suggestLabelKey,
  suggestDefaultLabel,
  primaryEvent,
  splitScreenImageArk,
  onValidateFields,
}) => {
  const { control } = useZionFormContext()
  const watchedFields = useWatch({ name: path, control }) || []

  return watchedFields?.map((field, index) => (
    <EventFormInput
      index={index}
      indexName={indexName}
      key={field?.id}
      field={field}
      eventIndex={eventIndex}
      SuggestComponent={SuggestComponent}
      suggestFieldType={suggestFieldType}
      suggestLabelKey={suggestLabelKey}
      suggestDefaultLabel={suggestDefaultLabel}
      primaryEvent={primaryEvent}
      splitScreenImageArk={splitScreenImageArk}
      onValidateFields={onValidateFields}
    />
  ))
}

export default FieldsSection
